// src/components/OzoneTool.js
import React, { useReducer, useEffect, useRef } from 'react';
import { fetchOzoneData } from '../services/apiService';
import { IoArrowForward, IoMailOutline, IoCallOutline, IoShareSocialOutline } from 'react-icons/io5';

const initialState = {
  urlText: "",
  loading: false,
  error: "",
  results: [],
  elapsed: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_URL":
      return { ...state, urlText: action.payload, error: '' };
    case "FETCH_START":
      return { ...state, loading: true, error: "", results: [], elapsed: 0 };
    case "TICK":
      return { ...state, elapsed: +(state.elapsed + 0.1).toFixed(1) };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, results: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

const OzoneTool = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const timerRef = useRef(null);

    const normalizeUrl = (u) => {
        const trimmed = u.trim();
        return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    };

    const handleFetch = async () => {
        if (!state.urlText.trim()) {
            dispatch({ type: 'FETCH_ERROR', payload: 'Please enter a website URL.' });
            return;
        }
        const url = normalizeUrl(state.urlText);

        dispatch({ type: "FETCH_START" });
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => dispatch({ type: "TICK" }), 100);

        try {
            const data = await fetchOzoneData(url);
            if (data.length === 0 || (data.every(d => d.emails.length === 0 && d.phones.length === 0 && d.social.length === 0))) {
                dispatch({ type: 'FETCH_ERROR', payload: 'No contact information found on this domain.' });
            } else {
                dispatch({ type: "FETCH_SUCCESS", payload: data });
            }
        } catch (err) {
            const msg = err.message.includes("invalid argument") ? "Invalid URL. Please check the format." : (err.message || "An unknown error occurred.");
            dispatch({ type: "FETCH_ERROR", payload: msg });
        } finally {
            clearInterval(timerRef.current);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleFetch();
    };

    useEffect(() => () => clearInterval(timerRef.current), []);

    return (
        <div className="mx-auto max-w-4xl px-6">
            <div className="bg-dark-card/40 backdrop-blur-lg border border-white/10 rounded-3xl p-8">
                <div className="flex items-center w-full bg-black/30 border border-white/10 rounded-2xl shadow-lg p-2">
                    <input
                        type="text"
                        value={state.urlText}
                        onChange={(e) => dispatch({ type: 'SET_URL', payload: e.target.value })}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter a website URL (e.g., oolook.in)"
                        className="w-full bg-transparent text-dark-text-primary placeholder:text-dark-text-secondary/50 border-none outline-none px-4 text-md"
                        disabled={state.loading}
                    />
                    <button
                        onClick={handleFetch}
                        disabled={state.loading}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-dark-accent-start rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                        {state.loading ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <IoArrowForward size={20} className="text-white" />
                        )}
                    </button>
                </div>
                {state.error && <p className="text-center text-sm text-red-400 mt-4">{state.error}</p>}

                {state.results.length > 0 && (
                    <div className="mt-8 space-y-4">
                        <p className="text-xs text-center text-dark-text-secondary">Found results in {state.elapsed}s</p>
                        {state.results.map((result) => (
                            <div key={result.url} className="bg-dark-card/60 border border-white/10 rounded-xl p-4">
                                <h4 className="font-semibold text-dark-text-primary truncate">{result.url}</h4>
                                <div className="mt-3 space-y-2 text-sm text-dark-text-secondary">
                                    <div className="flex items-start gap-3"><IoMailOutline className="mt-1 flex-shrink-0" /> <span>{result.emails.join(', ') || 'N/A'}</span></div>
                                    <div className="flex items-start gap-3"><IoCallOutline className="mt-1 flex-shrink-0" /> <span>{result.phones.join(', ') || 'N/A'}</span></div>
                                    <div className="flex items-start gap-3"><IoShareSocialOutline className="mt-1 flex-shrink-0" /> <span>{result.social.join(', ') || 'N/A'}</span></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OzoneTool;